import { useState, type FormEvent } from 'react';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    if (honeypot && typeof honeypot === 'string') {
      formData.delete('company_alt');
    }

    const textarea = form.querySelector('textarea[name="inquiry"]') as HTMLTextAreaElement | null;
    if (textarea && textarea.value.length > 500) {
      setFormError('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setFormStatus('loading');

    try {
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          urlParams.append(key, value);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9aghpl86d4i8fhr90e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // response not JSON
      }

      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '送信に失敗しました。お手数ですが、お電話またはLINEにてお問い合わせください。';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('ネットワークエラーが発生しました。お手数ですが、お電話またはLINEにてお問い合わせください。');
      setFormStatus('error');
    }
  };

  return (
    <section
      id="contact"
      className="relative w-full py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20soft%20Kyoto%20traditional%20wooden%20facade%20at%20golden%20hour%20with%20subtle%20lantern%20light%2C%20muted%20warm%20neutral%20tones%2C%20calm%20trustworthy%20professional%20atmosphere%2C%20editorial%20photography%20style%2C%20simple%20clean%20background%2C%20high%20detail%20cinematic%20composition&width=1920&height=900&seq=ats-contact-bg-01&orientation=landscape"
          alt="京都の落ち着いた街並み"
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/85 to-foreground-950/90"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="font-mincho text-primary-300 tracking-widest text-sm md:text-base mb-3">
            CONTACT
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-background-50 leading-snug">
            まずは<span className="text-primary-400">無料相談</span>から
          </h2>
          <p className="mt-5 text-background-50/85 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            京都 不用品回収・京都 遺品整理・京都 ゴミ屋敷清掃、
            どんなご相談も無料でお受けします。お気軽にお問い合わせください。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto mb-10">
          <a
            href="tel:0120024747"
            className="group p-8 rounded-lg bg-background-50 hover:bg-background-100 border border-background-200 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-md bg-primary-500 text-background-50 group-hover:bg-primary-600 transition-colors">
                <i className="ri-phone-fill text-2xl"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500 tracking-widest mb-1">
                  お電話でのお問い合わせ
                </p>
                <p className="text-2xl md:text-3xl font-mincho font-bold text-foreground-950">
                  0120-02-4747
                </p>
              </div>
            </div>
            <div className="pt-5 border-t border-background-200 text-sm text-foreground-700 space-y-1">
              <p className="flex items-center gap-2">
                <i className="ri-time-line text-primary-600"></i>
                受付 9:00〜17:00 / 年中無休
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-truck-line text-primary-600"></i>
                京都市・即日対応可能
              </p>
            </div>
          </a>

          <a
            href="https://line.me/ti/p/520wvids"
            target="_blank"
            rel="noopener noreferrer"
            className="group p-8 rounded-lg bg-accent-700 hover:bg-accent-800 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-md bg-background-50 text-accent-700">
                <i className="ri-line-fill text-3xl"></i>
              </div>
              <div>
                <p className="text-xs text-background-50/85 tracking-widest mb-1">
                  LINEでのお問い合わせ
                </p>
                <p className="text-2xl md:text-3xl font-mincho font-bold text-background-50">
                  LINEで写真見積り
                </p>
              </div>
            </div>
            <div className="pt-5 border-t border-background-50/30 text-sm text-background-50/95 space-y-1">
              <p className="flex items-center gap-2">
                <i className="ri-image-line"></i>
                写真を送るだけで概算お見積り
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-chat-smile-2-line"></i>
                24時間受付 / ご返信は営業時間内
              </p>
            </div>
          </a>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-10">
            <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              フォームからのお問い合わせ
            </h3>
            <p className="text-sm text-foreground-600 mb-8">
              必要事項をご入力のうえ、送信してください。内容を確認後、担当者よりご連絡いたします。
            </p>

            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-6">
                  <i className="ri-check-line text-3xl text-primary-600"></i>
                </div>
                <p className="text-lg md:text-xl font-bold text-foreground-950 mb-3">
                  お問い合わせありがとうございます。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  内容を確認のうえ、担当者よりご連絡いたします。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                noValidate
              >
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-foreground-700 mb-2"
                      >
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        placeholder="例：山田 太郎"
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-sm font-medium text-foreground-700 mb-2"
                      >
                        電話番号 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        required
                        placeholder="例：090-1234-5678"
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-sm font-medium text-foreground-700 mb-2"
                    >
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      placeholder="例：example@email.com"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-inquiry"
                      className="block text-sm font-medium text-foreground-700 mb-2"
                    >
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact-inquiry"
                      name="inquiry"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="お問い合わせ内容をご記入ください（例：不用品回収の見積り希望、遺品整理のご相談 など）"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors resize-y"
                    ></textarea>
                    <p className="mt-1 text-xs text-foreground-400">
                      500文字以内でご入力ください
                    </p>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                  />

                  {formError && (
                    <div className="p-4 rounded-md bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full md:w-auto whitespace-nowrap px-10 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-bold text-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        送信する
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="mt-10 text-center text-background-50/75 text-xs md:text-sm">
          <p>
            法人・不動産会社様のお見積り依頼、継続対応のご相談もお電話・LINEにて承ります。
          </p>
        </div>
      </div>
    </section>
  );
}